// The project room (brief section 8.3): goal, audience, stage, owner, capacity, and the project's
// tasks, decisions, knowledge and conversations in one place.
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useContextWorkspace } from '../components/Layout';
import { DecisionRow, TaskRow } from '../components/records';
import { Audience, Chip, Demo, Empty, ErrorBox, Loading, LockIcon, PageHead } from '../components/ui';
import type { ProjectRoom as Room } from '../lib/types';
import { useApi } from '../lib/useApi';
import { KnowledgeForm, KnowledgeRow, ProjectForm, TaskForm } from './Projects';

export function ProjectRoom() {
  const ws = useContextWorkspace()!;
  const { pid } = useParams();
  const [params] = useSearchParams();
  const room = useApi<Room>(`/api/w/${ws.slug}/projects/${pid}`);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState<'task' | 'knowledge' | null>(null);
  const focusTask = params.get('task');
  const focusKnowledge = params.get('knowledge');

  useEffect(() => {
    const id = focusTask ? `task-${focusTask}` : focusKnowledge ? `knowledge-${focusKnowledge}` : null;
    if (id && room.data) document.getElementById(id)?.scrollIntoView({ block: 'center' });
  }, [focusTask, focusKnowledge, room.data]);

  if (room.error) return <ErrorBox error={room.error} retry={room.reload} />;
  const p = room.data;
  if (!p) return <Loading what="Opening the project" />;

  return (
    <>
      <p className="crumbs">
        <Link to={`/w/${ws.slug}/projects`}>Projects</Link> ›
      </p>
      <PageHead
        title={p.title}
        sub={
          <span className="row gap wrap">
            <Chip value={p.stage} />
            <Audience item={p} />
            <Demo show={p.demo} />
          </span>
        }
        actions={
          <>
            <Link className="btn" to={`/w/${ws.slug}/assistant?project=${p.id}`}>
              Ask about this project
            </Link>
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Edit
            </button>
          </>
        }
      />
      <section className="panel facts">
        <dl>
          <dt>Goal</dt>
          <dd>{p.goal || <span className="muted">not written yet</span>}</dd>
          <dt>Audience</dt>
          <dd>{p.audience || <span className="muted">not written yet</span>}</dd>
          <dt>Accountable</dt>
          <dd>{p.owner?.name ?? <span className="muted">nobody yet</span>}</dd>
          <dt>Capacity</dt>
          <dd>{p.capacity_hours_per_week !== null ? `${p.capacity_hours_per_week} hours / week` : <span className="muted">not set</span>}</dd>
          <dt>Costs</dt>
          <dd className="muted">Tracked from milestone 4 (budgets).</dd>
        </dl>
      </section>
      <div className="room-grid">
        <section className="panel">
          <div className="row between">
            <h2>Tasks</h2>
            <button type="button" className="btn small" onClick={() => setAdding('task')}>
              Add task
            </button>
          </div>
          {p.task_list.length ? (
            <ul className="recs">
              {p.task_list.map((t) => (
                <TaskRow key={t.id} task={t} slug={ws.slug} onChanged={() => void room.reload()} highlight={t.id === focusTask} />
              ))}
            </ul>
          ) : (
            <Empty>No tasks yet.</Empty>
          )}
        </section>
        <section className="panel">
          <h2>Decisions</h2>
          {p.decision_list.length ? (
            <ul className="recs">
              {p.decision_list.map((d) => (
                <DecisionRow key={d.id} d={d} slug={ws.slug} />
              ))}
            </ul>
          ) : (
            <Empty>No decisions recorded for this project.</Empty>
          )}
        </section>
        <section className="panel">
          <div className="row between">
            <h2>Knowledge</h2>
            <button type="button" className="btn small" onClick={() => setAdding('knowledge')}>
              Add
            </button>
          </div>
          {p.knowledge_list.length ? (
            <ul className="recs">
              {p.knowledge_list.map((k) => (
                <KnowledgeRow key={k.id} k={k} highlight={k.id === focusKnowledge} />
              ))}
            </ul>
          ) : (
            <Empty>Nothing recorded yet.</Empty>
          )}
        </section>
        <section className="panel">
          <h2>Conversations</h2>
          {p.conversations.length ? (
            <ul className="recs">
              {p.conversations.map((c) => (
                <li key={c.id} className="rec">
                  <Link to={`/w/${ws.slug}/assistant/${c.id}`}>
                    {c.private && <LockIcon />} {c.title}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No conversation about this project yet.</Empty>
          )}
        </section>
      </div>
      {editing && (
        <ProjectForm
          project={p}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void room.reload();
          }}
        />
      )}
      {adding === 'task' && (
        <TaskForm
          projectId={p.id}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            void room.reload();
          }}
        />
      )}
      {adding === 'knowledge' && (
        <KnowledgeForm
          projectId={p.id}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            void room.reload();
          }}
        />
      )}
    </>
  );
}
